// ABOUTME: Header component for the production schedule section.
// ABOUTME: Contains title, toggles, shop selector, and refresh button.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Switch, Chip, Select, MenuItem, FormControl, IconButton, Tooltip } from '@mui/material';
import { NightsStay as NightsStayIcon } from '@mui/icons-material';
import { ArrowPathIcon, ChevronDownIcon } from './icons/HeroIcons';
import TrendSlider from './TrendSlider';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function ScheduleHeader({
  schedule,
  isNextDayMode,
  onNextDayModeChange,
  groupByCategory,
  onGroupByCategoryChange,
  shops,
  currentShopId,
  onShopChange,
  onDetailedShopSelector,
  shopsLoading,
  onRefresh,
  showShopSelector,
  trendWeight,
  onTrendWeightChange,
  onResetTrendWeight,
  trendWeightLoading,
  savedTrendWeight,
  chartExpanded,
  onChartExpandedChange,
  isVirtualShop = false,
}) {
  const { t } = useTranslation();

  const Toggle = ({ label, checked, onChange }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: '#f3f4f6',
        borderRadius: '9999px',
        pl: 2,
        pr: 0.5,
        py: 0.5,
      }}
    >
      <Box
        sx={{
          fontSize: '0.875rem',
          color: '#6b7280',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Box>
      <Switch
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        size="small"
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#10b981',
          },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: '#10b981',
          },
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#1f2937',
          }}
        >
          {t('dashboard.schedule.title')}
        </Box>

        {/* Show next opening info when available (auto-enabled after closing or manually via toggle) */}
        {schedule?.next_opening_info && (
          <Chip
            icon={<NightsStayIcon sx={{ fontSize: 16 }} />}
            label={`${schedule.next_opening_info.date} ${t(`common.${DAY_KEYS[schedule.next_opening_info.day_of_week]}`)} ${schedule.next_opening_info.opening_time}`}
            size="small"
            sx={{
              bgcolor: '#dbeafe',
              color: '#1d4ed8',
              fontWeight: 500,
              '& .MuiChip-icon': { color: '#1d4ed8' },
            }}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Trend Slider and Chart Toggle - member-level features, hidden for virtual shops */}
        {!isVirtualShop && (
          <TrendSlider
            trendWeight={trendWeight}
            onTrendWeightChange={onTrendWeightChange}
            onResetTrendWeight={onResetTrendWeight}
            trendWeightLoading={trendWeightLoading}
            savedTrendWeight={savedTrendWeight}
            chartExpanded={chartExpanded}
            onChartExpandedChange={onChartExpandedChange}
          />
        )}

        {/* Only show next day toggle when shop is OPEN */}
        {!schedule?.is_after_closing && (
          <Toggle
            label={t('dashboard.schedule.showNextDay')}
            checked={isNextDayMode}
            onChange={onNextDayModeChange}
          />
        )}

        {/* Group by category toggle */}
        <Toggle
          label={t('dashboard.groupByCategory')}
          checked={groupByCategory}
          onChange={onGroupByCategoryChange}
        />

        {/* Refresh button */}
        <Tooltip title={t('common.refresh')}>
          <IconButton
            onClick={onRefresh}
            sx={{
              bgcolor: '#f3f4f6',
              borderRadius: '8px',
              width: 40,
              height: 40,
              '&:hover': { bgcolor: '#e5e7eb' },
            }}
          >
            <ArrowPathIcon width={20} height={20} style={{ color: '#374151' }} />
          </IconButton>
        </Tooltip>

        {/* Shop Selector - only shown in test mode */}
        {showShopSelector && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={currentShopId || ''}
              onChange={(e) => {
                if (e.target.value === '__detailed__') {
                  onDetailedShopSelector();
                } else {
                  onShopChange(e);
                }
              }}
              disabled={shopsLoading || shops.length === 0}
              displayEmpty
              IconComponent={(props) => (
                <ChevronDownIcon {...props} width={18} height={18} style={{ marginRight: 8, color: '#6b7280', cursor: 'pointer' }} />
              )}
              sx={{
                bgcolor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  py: 1,
                  fontWeight: 500,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
                    mt: 1,
                    '& .MuiList-root': {
                      py: 1,
                    },
                  },
                },
              }}
            >
              <MenuItem
                value="__detailed__"
                sx={{
                  color: '#9ca3af',
                  fontSize: '0.875rem',
                  borderBottom: '1px solid #f3f4f6',
                  mx: 1,
                  mb: 1,
                  borderRadius: '6px',
                  '&:hover': {
                    bgcolor: '#f9fafb',
                  },
                }}
              >
                {t('common.detailedShopSelector')}
              </MenuItem>
              {shops.map((shop) => (
                <MenuItem
                  key={shop.shop_id}
                  value={shop.shop_id}
                  sx={{
                    mx: 1,
                    borderRadius: '6px',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: '#fff7ed',
                    },
                    '&.Mui-selected': {
                      bgcolor: '#FCECDD',
                      '&:hover': {
                        bgcolor: '#fff7ed',
                      },
                    },
                  }}
                >
                  {shop.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
    </Box>
  );
}

export default ScheduleHeader;
